<script lang="ts">
	/**
	 * Invisible sentinel that fires `becomeVisible` when it scrolls into view.
	 *
	 * Drop it directly after the last item of a paginated list; when the user
	 * scrolls far enough to reveal it, load the next page. This is the whole
	 * infinite-scroll mechanism in the app — there is no scroll-position maths
	 * anywhere else.
	 *
	 * Uses an IntersectionObserver rather than a scroll listener so it works
	 * unchanged inside nested scroll containers (drawers, kanban columns,
	 * `ScrollableDiv`) — the observer walks clipping ancestors for us, and it
	 * costs nothing when the page is idle.
	 *
	 * The event fires only on the false→true edge. After appending a page, call
	 * `resetVisibilityTracking()` then `updateState()` so a sentinel that is
	 * *still* on screen (short page, tall viewport) keeps paging instead of
	 * stalling.
	 */
	import { createEventDispatcher, onMount } from 'svelte';

	let divElement: HTMLDivElement;

	let className: string = '';
	export { className as class };

	/**
	 * How far outside the viewport the sentinel counts as visible — a CSS
	 * margin string. The default preloads roughly one screen early so the next
	 * page is usually already in place by the time the user reaches it.
	 */
	export let rootMargin: string = '300px';

	/** True once the sentinel has been seen; suppresses repeat events. Bindable. */
	export let isPreviouslyInViewport: boolean = false;

	/** Renders the current state as text. Development aid only. */
	export let debug: boolean = false;

	const dispatch = createEventDispatcher<{ becomeVisible: Record<string, never> }>();

	/** Live geometry check — used by `updateState()` and available to callers. */
	export function isInViewport(): boolean {
		if (!divElement) return false;

		const rect: DOMRect = divElement.getBoundingClientRect();
		return (
			rect.bottom >= 0 &&
			rect.right >= 0 &&
			rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
			rect.left <= (window.innerWidth || document.documentElement.clientWidth)
		);
	}

	/** Re-arms the `becomeVisible` edge so it can fire again after a page append. */
	export function resetVisibilityTracking(): void {
		isPreviouslyInViewport = false;
	}

	/** Re-evaluates visibility now, firing `becomeVisible` on the rising edge. */
	export function updateState(fireEvent: boolean = true): void {
		const isNowInViewport: boolean = isInViewport();

		if (!isPreviouslyInViewport && isNowInViewport) {
			isPreviouslyInViewport = true;
			if (fireEvent) {
				dispatch('becomeVisible', {});
			}
		}
	}

	onMount(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				const isNowInViewport = entries.some((entry) => entry.isIntersecting);

				if (!isPreviouslyInViewport && isNowInViewport) {
					isPreviouslyInViewport = true;
					dispatch('becomeVisible', {});
				}
			},
			{ rootMargin }
		);

		observer.observe(divElement);
		return () => observer.disconnect();
	});
</script>

<div class="inline-block {className}" bind:this={divElement}>
	{#if debug}
		{isPreviouslyInViewport ? 'visible' : 'not visible'}
	{/if}
	<slot />
</div>
